import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import {
  formatBetterDocRecommendationState,
  getBetterDocEnumLabel,
} from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { TEST_CASE_OUTCOME_TAG_VARIANTS } from "@/modules/admin/better-doc/lib/better-doc-tag-variants"
import { formatBetterDocValue } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocTestCaseResult } from "@/modules/admin/better-doc/services/useSendBetterDocTestCaseMutation"
import { de_BetterDocTestCaseScenario } from "@/types/__generated__/union-types"

export type BetterDocTestCaseResultRow = {
  resultNumber: number
  scenario: de_BetterDocTestCaseScenario
  result: BetterDocTestCaseResult
}

type BetterDocTestCaseResultTableProps = {
  rows: ReadonlyArray<BetterDocTestCaseResultRow>
}

export const BetterDocTestCaseResultTable = ({ rows }: BetterDocTestCaseResultTableProps) => {
  const { t } = useTranslation()

  return (
    <BetterDocTable data-testid="better-doc-admin-test-case-result-table">
      <thead>
        <tr>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.insuranceCountry.label")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.scenario")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.outcome")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.httpStatusCode")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.betterDocState")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.caseId")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.recommendationId")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.errorReason")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.testCaseSender.session")}</BetterDocTableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ resultNumber, scenario, result }) => (
          <tr
            key={resultNumber}
            className="hover:bg-surface-hovered"
            data-testid="better-doc-admin-test-case-result-row">
            <BetterDocTableCell>
              {getBetterDocEnumLabel(t, "insuranceCountry", result.insuranceCountry)}
            </BetterDocTableCell>
            <BetterDocTableCell>{getBetterDocEnumLabel(t, "testCaseScenario", scenario)}</BetterDocTableCell>
            <BetterDocTableCell>
              <Tag
                type={TEST_CASE_OUTCOME_TAG_VARIANTS[result.outcome]}
                label={getBetterDocEnumLabel(t, "testCaseOutcome", result.outcome)}
              />
            </BetterDocTableCell>
            <BetterDocTableCell>{formatBetterDocValue(result.httpStatusCode)}</BetterDocTableCell>
            <BetterDocTableCell>{formatBetterDocRecommendationState(t, result.betterDocState)}</BetterDocTableCell>
            <BetterDocTableCell className="break-all">{formatBetterDocValue(result.caseId)}</BetterDocTableCell>
            <BetterDocTableCell className="break-all">
              {formatBetterDocValue(result.recommendationId)}
            </BetterDocTableCell>
            <BetterDocTableCell>{formatBetterDocValue(result.errorReason)}</BetterDocTableCell>
            <BetterDocTableCell>
              <BetterDocSessionLink sessionId={result.sessionId} />
            </BetterDocTableCell>
          </tr>
        ))}
      </tbody>
    </BetterDocTable>
  )
}
